"use client";
import * as React from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  resolveCircleActionVariant,
  type CircleActionVariant} from "./circle-action-variants";
import {
  LiquidPill,
  type LiquidPillAppearance,
  type LiquidPillSize,
} from "./liquid-pill";
import {
  LiquidSurface,
  type LiquidDeformAxis,
  type LiquidSurfaceBounceIntensity,
  type LiquidSurfaceShadow,
  type LiquidSurfaceStyle,
} from "./liquid-surface";
import { LiquidPillGroupVertical } from "./liquid-pill-group-vertical";
import { Z_INDEX } from "./z-index";
export interface LiquidPillDropdownItem {
  value: string;
  label: React.ReactNode;
  icon?: React.ElementType | React.ReactNode;
  variant?: CircleActionVariant;
  disabled?: boolean;
}
export interface LiquidPillDropdownProps {
  items?: LiquidPillDropdownItem[];
  data?: any[];
  labelKey?: string;
  valueKey?: string;
  value?: any;
  onSelect?: (value: string, item?: any) => void;
  /** @deprecated legacy rsuite-style alias */
  onChange?: (value: string, item?: any) => void;
  /** @deprecated accepted for legacy call sites */
  onSearch?: (value: string) => void;
  onHoverChange?: (value: string | null) => void;
  placeholder?: React.ReactNode;
  icon?: React.ElementType | React.ReactNode;
  variant?: CircleActionVariant;
  disabled?: boolean;
  active?: boolean;
  size?: LiquidPillSize;
  collapsedShape?: "circle" | "pill" | "rounded";
  showLabelWhenClosed?: boolean;
  /** LiquidPill / LiquidSurface: "normal" | "tinted" */
  appearance?: LiquidPillAppearance;
  /** LiquidSurface shadow del trigger */
  shadow?: LiquidSurfaceShadow;
  /** Partícula especular del trigger */
  cursorGlow?: boolean;
  /** Variante visual compuesta del LiquidSurface del trigger */
  surfaceStyle?: LiquidSurfaceStyle;
  itemClassName?: string;
  className?: string;
  style?: React.CSSProperties;
  block?: boolean;
  searchable?: boolean;
  cleanable?: boolean;
  loading?: boolean;
  menuMaxHeight?: number;
  renderMenu?: (menu: React.ReactNode) => React.ReactNode;
  renderMenuItem?: (label: React.ReactNode, item: any) => React.ReactNode;
  renderValue?: (value: any, item: any) => React.ReactNode;
  closeOnSelect?: boolean;
  /** Permite decidir por item si el menú debe cerrarse después de seleccionar. */
  shouldCloseOnSelect?: (value: string, item?: any) => boolean;
  /** Tamaño de las filas del menú (LiquidPillGroupVertical). Default `xxs`. */
  listSize?: "xxs" | "xs" | "sm" | "md";
  /** Intensidad del rebote/deformación (trigger + filas del menú). Default `strong`. */
  bounceIntensity?: LiquidSurfaceBounceIntensity;
  /** Eje de deformación del trigger al hover. Default `both`. */
  deformAxis?: LiquidDeformAxis;
  /** Eje de deformación de la burbuja del menú. Default `vertical`. */
  listDeformAxis?: LiquidDeformAxis;
  /** z-index del panel portado. Usar `Z_INDEX.MODAL + 100` dentro de modales. */
  menuZIndex?: number;
  /** Activa el material reactivo al fondo en el trigger (LiquidSurface). */
  reactive?: boolean;
  "aria-label"?: string;
}
function renderIcon(icon: LiquidPillDropdownItem["icon"], size = 16) {
  if (!icon) return null;
  if (React.isValidElement(icon)) return icon;
  return React.createElement(icon as React.ElementType, { size, strokeWidth: 1.75 });
}
const COLLAPSED_BOX: Record<Exclude<LiquidPillSize, "card-cta">, { size: number; iconSize: number }> = {
  xxs: { size: 20, iconSize: 10 },
  xs: { size: 24, iconSize: 14 },
  sm: { size: 28, iconSize: 14 },
  md: { size: 36, iconSize: 16 },
  lg: { size: 44, iconSize: 18 },
  xl: { size: 56, iconSize: 20 },
  "2xl": { size: 64, iconSize: 22 }};
const OPEN_MS  = 520;
const CLOSE_MS = 280;
const EASE     = "cubic-bezier(0.34, 1.2, 0.64, 1)";
/**
 * Dropdown l\u00edquido con posicionamiento **dual por estado** (regla design language \u00a76.5):
 *
 * - **Inactivo (colapsado)**: el trigger `<LiquidPill>` vive in-flow con
 *   `position: static` \u2014 posici\u00f3n por defecto del DOM. **No** se portala.
 *   **No** tiene `z-index`. Por tanto **nunca se proyecta sobre el navbar ni
 *   sobre ninguna capa superior** estando en idle.
 *
 * - **Activo (abierto)**: el panel se monta en un portal a `<body>` con
 *   `position: fixed` y `z-index: Z_INDEX.DROPDOWN` (3000). Esto le permite
 *   escapar `overflow:hidden` de ancestros (cards, contenedores con clipping)
 *   pero respeta la jerarqu\u00eda de capas: queda **por debajo** del navbar
 *   (6000), modal (11000) y toast (12000).
 *
 * Animaci\u00f3n de morph: el trigger in-flow se desvanece (`opacity: 0`) mientras
 * el panel portado crece desde la posici\u00f3n exacta del trigger, simulando una
 * transformaci\u00f3n continua. Al cerrar, el panel se encoge de vuelta al tama\u00f1o
 * del trigger antes de desmontarse, y el trigger vuelve a aparecer.
 */
export function LiquidPillDropdown({
  items: itemsProp,
  data,
  labelKey = "label",
  valueKey = "value",
  value,
  onSelect,
  onChange,
  onHoverChange,
  placeholder,
  icon,
  variant,
  disabled = false,
  active = false,
  size = "md",
  collapsedShape = "circle",
  showLabelWhenClosed = false,
  appearance = "normal",
  shadow = "default",
  cursorGlow = true,
  surfaceStyle,
  className,
  style,
  closeOnSelect = true,
  shouldCloseOnSelect,
  listSize = "xxs",
  bounceIntensity = "strong",
  deformAxis = "both",
  listDeformAxis = "vertical",
  menuZIndex = Z_INDEX.DROPDOWN,
  reactive = false,
  "aria-label": ariaLabel}: LiquidPillDropdownProps) {
  const triggerRef = React.useRef<HTMLSpanElement>(null);
  const panelRef   = React.useRef<HTMLDivElement>(null);
  const closeTimerRef = React.useRef<number | null>(null);
  const [mounted, setMounted]             = React.useState(false);
  // `open`: portal mounted (panel is in DOM, even if shrinking out).
  // `panelExpanded`: panel has expanded styles (triggers transitions).
  // We separate these so we can run an exit animation before unmount.
  const [open, setOpen]                   = React.useState(false);
  const [panelExpanded, setPanelExpanded] = React.useState(false);
  const [rect, setRect]                   = React.useState({ top: 0, left: 0 });

  // Map data and custom keys to LiquidPillDropdownItem format if data is provided
  const items: LiquidPillDropdownItem[] = React.useMemo(() => {
    if (itemsProp) return itemsProp;
    if (data) {
      return data.map((item) => ({
        value: String(item[valueKey]),
        label: React.isValidElement(item[labelKey]) ? item[labelKey] : String(item[labelKey]),
        icon: item.icon,
        variant: item.variant,
        disabled: item.disabled,
      }));
    }
    return [];
  }, [itemsProp, data, labelKey, valueKey]);

  const selectedItem    = items.find((item) => item.value === value);
  const displayVariant  = resolveCircleActionVariant(
    selectedItem?.variant ?? variant ?? "slate"
  );
  const triggerLabel = selectedItem?.label ?? placeholder;
  const triggerIcon  = selectedItem?.icon  ?? icon;
  // Un círculo colapsado sin icono quedaría completamente vacío — degradar a
  // pill para que muestre la etiqueta/placeholder y el chevron.
  const effectiveCollapsedShape =
    collapsedShape === "circle" && !triggerIcon ? "pill" : collapsedShape;
  const shouldShowClosedLabel =
    showLabelWhenClosed || effectiveCollapsedShape !== "circle";
  const box          = COLLAPSED_BOX[size];
  const listIconSize   = listSize === "xxs" ? 12 : listSize === "xs" ? 14 : 16;
  const listCheckClass = listSize === "xxs" ? "h-3 w-3" : "h-3.5 w-3.5";
  React.useEffect(() => { setMounted(true); }, []);
  // Captura coords del trigger in-flow para anclar el panel portado.
  const captureRect = React.useCallback(() => {
    if (!triggerRef.current || typeof window === "undefined") return;
    const r = triggerRef.current.getBoundingClientRect();
    setRect({ top: r.top, left: r.left });
  }, []);
  // Mantiene el panel anclado al trigger si la p\u00e1gina hace scroll/resize.
  React.useEffect(() => {
    if (!open || typeof window === "undefined") return;
    captureRect();
    const update = () => captureRect();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    let ro: ResizeObserver | undefined;
    if (triggerRef.current && "ResizeObserver" in window) {
      ro = new ResizeObserver(update);
      ro.observe(triggerRef.current);
    }
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
      ro?.disconnect();
    };
  }, [open, captureRect]);
  // Trigger enter animation after mount: render once at collapsed size, then
  // rAF expands to full panel size for a smooth CSS transition.
  React.useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      setPanelExpanded(true);
    });
    return () => cancelAnimationFrame(id);
  }, [open]);
  const clearCloseTimer = () => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const openPanel = () => {
    if (disabled) return;
    clearCloseTimer();
    captureRect();
    setOpen(true);
    // panelExpanded queda en false hasta que el rAF del useEffect lo dispare.
    setPanelExpanded(false);
  };
  const closePanel = React.useCallback(() => {
    clearCloseTimer();
    setPanelExpanded(false);
    onHoverChange?.(null);
    // Esperar a que termine la animaci\u00f3n de shrink antes de desmontar el portal.
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, CLOSE_MS);
  }, [onHoverChange]);
  React.useEffect(() => () => clearCloseTimer(), []);
  const handleTriggerClick = () => {
    if (open) closePanel();
    else openPanel();
  };
  const handleSelect = (item: LiquidPillDropdownItem) => {
    if (item.disabled) return;
    onSelect?.(item.value, item);
    onChange?.(item.value, item);
    onHoverChange?.(null);
    const shouldClose = shouldCloseOnSelect
      ? shouldCloseOnSelect(item.value, item)
      : closeOnSelect;
    if (shouldClose) closePanel();
  };
  // Cierre por click fuera / Escape
  React.useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node) {
        if (triggerRef.current?.contains(target)) return;
        if (panelRef.current?.contains(target)) return;
      }
      closePanel();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closePanel();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, closePanel]);
  // \u2500\u2500 Trigger in-flow (siempre montado) \u2500\u2500
  // - Position default (static via wrapper inline-block).
  // - Sin z-index.
  // - Se desvanece (opacity 0 + pointer-events none) cuando el panel est\u00e1 abierto
  //   para que visualmente el panel ocupe su lugar sin solapar.
  const triggerContent = (
    <>
      {renderIcon(triggerIcon, box.iconSize)}
      {shouldShowClosedLabel && triggerLabel != null && (
        <span className="max-w-[10rem] truncate whitespace-nowrap text-[12.5px] font-semibold tracking-[-0.01em]">
          {triggerLabel}
        </span>
      )}
      {effectiveCollapsedShape !== "circle" && (
        <ChevronDown className="h-3.5 w-3.5 shrink-0" />
      )}
    </>
  );
  return (
    <>
      <span
        ref={triggerRef}
        className={cn(
          "relative inline-block align-top",
          open && "pointer-events-none opacity-0",
          "liquid-pill-dropdown-trigger",
          className
        )}
        style={
          effectiveCollapsedShape === "circle"
            ? { width: `${box.size}px`, height: `${box.size}px` }
            : { minHeight: `${box.size}px` }
        }
      >
        <LiquidPill
          as="button"
          type="button"
          variant={displayVariant}
          appearance={appearance}
          shadow={shadow}
          cursorGlow={cursorGlow}
          surfaceStyle={surfaceStyle}
          shape={effectiveCollapsedShape === "rounded" ? "rounded" : effectiveCollapsedShape === "pill" ? "pill" : "circle"}
          size={size}
          active={active}
          disabled={disabled}
          hoverDeform={!open}
          hoverBounce={!open}
          bounceIntensity={bounceIntensity}
          deformAxis={deformAxis}
          reactive={reactive}
          onClick={handleTriggerClick}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel}
        >
          <span className="relative z-[3] inline-flex items-center gap-2">
            {triggerContent}
          </span>
        </LiquidPill>
      </span>
      {/* \u2500\u2500 Panel portado (solo cuando open) \u2500\u2500
          - position: fixed (escapa overflow ancestros como cards con overflow:hidden)
          - z-index: DROPDOWN (3000) \u2014 por debajo de NAVBAR (6000), MODAL (11000), TOAST (12000)
          - origen visual: el panel nace en las coords exactas del trigger y crece desde ah\u00ed. */}
      {mounted && open && typeof document !== "undefined" && createPortal(
        <div
          ref={panelRef}
          style={{
            position: "fixed",
            top:     rect.top,
            left:    rect.left,
            width:   `${box.size}px`,
            height:  `${box.size}px`,
            zIndex:  menuZIndex}}
        >
          <div
            className={cn(
              "liquid-pill-dropdown absolute right-0 top-0 overflow-hidden",
              panelExpanded && "liquid-pill-dropdown--is-open",
            )}
            style={{
              transitionProperty: "min-width, max-width, width, min-height, max-height, padding",
              transitionDuration: `${panelExpanded ? OPEN_MS : CLOSE_MS}ms`,
              transitionTimingFunction: EASE,
              minWidth:  panelExpanded ? "auto" : `${box.size}px`,
              maxWidth:  panelExpanded ? "min(20rem, calc(100vw - 2rem))" : `${box.size}px`,
              minHeight: panelExpanded ? `${box.size + 8}px` : `${box.size}px`,
              maxHeight: panelExpanded ? "460px" : `${box.size}px`,
              padding:   panelExpanded ? "8px 8px" : "0px",
              borderRadius: "min(50%, 20px)"}}
          >
            <LiquidSurface
              as="div"
              variant={displayVariant}
              tone="tinted"
              shape="pill"
              size="free"
              interactive={false}
              cursorGlow={false}
              hoverDeform={false}
              hoverBounce={false}
              style={{ position: "absolute", inset: 0, borderRadius: "inherit" }}
            />
            {/* Superficie blanca s\u00f3lida / frosted que aparece sincronizada con la expansi\u00f3n */}
            <span
              aria-hidden
              className="liquid-pill-dropdown-white-surface pointer-events-none absolute inset-0"
              style={{
                opacity: panelExpanded ? 1 : 0,
                transition: `opacity ${panelExpanded ? OPEN_MS : CLOSE_MS}ms ${EASE}`,
                borderRadius: "inherit"}}
            />
            <span className="relative z-[3] flex w-full min-w-0 flex-col items-stretch">
              <span
                aria-hidden={!panelExpanded}
                className="liquid-pill-dropdown-list flex min-w-0 flex-col overflow-hidden"
                style={{
                  transition: `max-height ${panelExpanded ? OPEN_MS : CLOSE_MS}ms ${EASE}, opacity ${Math.min(panelExpanded ? OPEN_MS : CLOSE_MS, 280)}ms ${EASE}`,
                  maxHeight: panelExpanded ? "380px" : "0px",
                  opacity:   panelExpanded ? 1 : 0,
                  pointerEvents: panelExpanded ? "auto" : "none"}}
              >
                <LiquidPillGroupVertical
                  items={items.map((item) => ({
                    value: item.value,
                    label: item.label,
                    icon: renderIcon(item.icon, listIconSize),
                    variant: item.variant,
                    disabled: item.disabled,
                    trailing: item.value === value ? (
                      <Check className={cn("shrink-0", listCheckClass)} strokeWidth={2.5} />
                    ) : null}))}
                  activeValue={value ?? null}
                  onChange={(v) => {
                    const item = items.find((i) => i.value === v);
                    if (item) handleSelect(item);
                  }}
                  onHover={onHoverChange}
                  size={listSize}
                  bounceIntensity={bounceIntensity}
                  deformAxis={listDeformAxis}
                />
              </span>
            </span>
          </div>
        </div>,
        document.body,
      )}
      <style jsx global>{`
        /* Panel wrapper: dimensiones y animación controladas por JS.
           El LiquidSurface dentro provee el fondo glass/frosted. */
        .liquid-pill-dropdown > .liquid-surface {
          background: rgba(255, 255, 255, 0.72) !important;
          backdrop-filter: blur(24px) saturate(1.4);
          -webkit-backdrop-filter: blur(24px) saturate(1.4);
        }
        .dark .liquid-pill-dropdown > .liquid-surface {
          background: rgba(16, 16, 20, 0.88) !important;
          backdrop-filter: blur(24px) saturate(1.02) brightness(0.68);
          -webkit-backdrop-filter: blur(24px) saturate(1.02) brightness(0.68);
        }
        .liquid-pill-dropdown > .liquid-surface::before,
        .liquid-pill-dropdown > .liquid-surface::after,
        .liquid-pill-dropdown > .liquid-surface > .liquid-surface__hover-overlay,
        .liquid-pill-dropdown > .liquid-surface > .liquid-surface__cursor-glow {
          display: none !important;
        }
        /* Superficie frosted que acompaña la expansión */
        .liquid-pill-dropdown-white-surface {
          background: rgba(255, 255, 255, 0.65);
          backdrop-filter: blur(20px) saturate(1.3);
          -webkit-backdrop-filter: blur(20px) saturate(1.3);
          box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.08);
        }
        .dark .liquid-pill-dropdown-white-surface {
          background: rgba(16, 16, 20, 0.84);
          backdrop-filter: blur(20px) saturate(1.02) brightness(0.66);
          -webkit-backdrop-filter: blur(20px) saturate(1.02) brightness(0.66);
          box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.03);
        }
        /* Sombra elevada cuando el panel está expandido */
        .liquid-pill-dropdown {
          transition-property:
            min-width, max-width, width, min-height, max-height, padding, box-shadow;
          transform-origin: top right;
        }
        .liquid-pill-dropdown--is-open {
          box-shadow:
            0 1px 2px rgba(15,23,42,0.04),
            0 12px 28px -10px rgba(15,23,42,0.22),
            0 24px 48px -20px rgba(15,23,42,0.18) !important;
        }
        .dark .liquid-pill-dropdown--is-open {
          box-shadow:
            0 1px 2px rgba(0,0,0,0.4),
            0 12px 28px -10px rgba(0,0,0,0.55),
            0 24px 48px -20px rgba(0,0,0,0.45) !important;
        }
        .liquid-pill-dropdown--is-open > .liquid-surface::before,
        .liquid-pill-dropdown--is-open > .liquid-surface::after {
          display: none;
        }
      `}</style>
    </>
  );
}
